import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Checkbox, Input } from 'antd'
import * as Rules from 'admin/lib/ValidationRules'
import { DualTimeZoneDateTimePicker } from 'admin/pages/RecordEdit/common/DualTimeZoneDateTimePicker'
import {
  Section,
  AutoCompleteFormItem,
  FormItem,
  FormSpace,
  SectionProps
} from 'admin/lib/Forms'

import {
  compareDatesWithOffSets,
  usDateFormatter
} from 'admin/lib/Functions/timeZoneHelper'
import { Waste as fields } from 'admin/lib/Records'

import { CarrierPresetSelect } from './CarrierPresetSelect'
import { FacilityPresetSelect } from './FacilityPresetSelect'
import { useDateAutoCopy } from 'admin/pages/RecordEdit/common/useDateAutoCopy'
import { KnownAddressInput } from 'admin/pages/RecordEdit/common/KnownAddressInput'
import {
  CheckAddress,
  CheckCarrierLicence,
  CheckCarrierName,
  CheckConsignmentOrDeliveryNumber,
  CheckNotes,
  CheckReceiverOrSupplierOrSenderOrCarrierReference,
  CheckSubContractor,
  CheckVehicleReg,
  CheckWastePermit,
  CheckWasteSICCode
} from 'admin/lib/SimpleDataValidation'
import styled from 'styled-components'

type Props = {
  createdAt: string
  projectName: string
  projectTimeZone: string
  projectCountry: string
  projectId: string
  isImperial: boolean
} & Omit<SectionProps, 'children'>

export const WasteData = (props: Props) => {
  const [removalDateWarning, setRemovalDateWarning] = useState<string | null>(
    null
  )

  const [removalExitDateWarning, setRemovalExitDateWarning] = useState<
    string | null
  >(null)

  const CREATION_DATE_WARNING_MESSAGE =
    'Entered date is after the Creation Date for this record'
  const EXIT_DATE_WARNING_MESSAGE = 'Exit Date cannot be before Arrival Date'

  const form = useFormContext()

  // Register non-interactive fields set from setValue
  useEffect(() => {
    form.register(fields.data.originSiteAddressEntered.path)
    form.register(fields.data.originSiteAddressCoordinates.path)
    form.register(fields.data.facilitySiteAddressEntered.path)
    form.register(fields.data.facilitySiteAddressCoordinates.path)
  }, [form])

  const removalDate = form.getValues('data.removalDate')
  const journeyDistanceMiles = form.watch('data.journey.distanceMiles')
  // Not ideal - but known address does not populate in time otherwise
  const knownFacilitySiteAddress = form.watch(
    fields.data.facilitySiteAddressKnown.path
  )

  const exitDatePopulation = useDateAutoCopy(
    fields.data.removalDate.path,
    fields.data.removalExitDate.path
  )

  const arrivalDateValidation = value => {
    setRemovalDateWarning(validateDateBeforeCreation(value))
    exitDateValidation(form.getValues('data.removalExitDate'))
    return true
  }
  const exitDateValidation = value => {
    if (value) {
      const warning =
        validateDateBeforeCreation(value) ?? validateExitAfterArrival(value)
      setRemovalExitDateWarning(warning)
    } else {
      setRemovalExitDateWarning(null)
    }
    return true
  }

  const validateDateBeforeCreation = value => {
    if (
      props.createdAt &&
      compareDatesWithOffSets(value, props.projectTimeZone, props.createdAt)
    ) {
      return CREATION_DATE_WARNING_MESSAGE
    } else {
      return null
    }
  }

  const validateExitAfterArrival = value => {
    const arrivalDate = form.getValues('data.removalDate')
    if (arrivalDate && new Date(arrivalDate) > new Date(value)) {
      return EXIT_DATE_WARNING_MESSAGE
    } else {
      return null
    }
  }

  const [permitPresentedChecked, setPermitPresentedChecked] = useState(true)

  const handlePermitPresentedChange = () => {
    form.setValue('data.facilityPermitPresented', !permitPresentedChecked)
    setPermitPresentedChecked(prev => !prev)
  }

  const setPermitPresentedTrue = () => {
    form.setValue('data.facilityPermitPresented', true)
    setPermitPresentedChecked(true)
  }

  useEffect(() => {
    setPermitPresentedChecked(form.getValues('data.facilityPermitPresented'))
  }, [form])

  useEffect(() => {
    let sub1, sub2
    if (props.isImperial) {
      sub1 = form.watch('data.removalDate')
      form.setValue(
        'data.removalDateUS',
        usDateFormatter(form.getValues('data.removalDate'))
      )

      sub2 = form.watch('data.removalExitDate')
      form.setValue(
        'data.removalExitDateUS',
        usDateFormatter(form.getValues('data.removalExitDate'))
      )
    }
    return () => {
      if (sub1 && sub1.unsubscribe) {
        sub1.unsubscribe()
      }
      if (sub2 && sub2.unsubscribe) {
        sub2.unsubscribe()
      }
    }
  }, [form, props.isImperial])

  const handleCarrierSelectChange = () => {
    // When select value changes trigger re-validation of Carrier Name & Licence
    document
      .getElementById('wasteDataCarrierName')
      ?.dispatchEvent(new Event('click', { bubbles: true }))
    document
      .getElementById('wasteDataCarrierLicence')
      ?.dispatchEvent(new Event('click', { bubbles: true }))

    // Return focus to Carrier Name
    document
      .getElementById('wasteDataCarrierName')
      ?.dispatchEvent(new Event('click', { bubbles: true }))
  }

  const handleFacilitySelectChange = () => {
    // When select value changes trigger re-validation of Facility Address and Permit
    document
      .getElementById('wasteDataFacilityPermit')
      ?.dispatchEvent(new Event('click', { bubbles: true }))

    const address = document.getElementById('wasteDataFacilitySiteAddress')
    address?.focus()
    address?.blur()

    // Return focus to Facility Operator Name
    document
      .getElementById('wasteDataFacilityOperatorName')
      ?.dispatchEvent(new Event('click', { bubbles: true }))
  }

  return (
    <>
      <Section {...props}>
        {({ firstInputRef }) => (
          <>
            <DualTimeZoneDateTimePicker
              id="WasteDataRemoval"
              label={fields.data.removalDate.label}
              name={fields.data.removalDate.path}
              imperialName={fields.data.removalDateUS.path}
              isImperial={props.isImperial}
              form={form}
              firstInputRef={firstInputRef}
              validateDate={arrivalDateValidation}
              warningMessage={removalDateWarning}
            />

            <DualTimeZoneDateTimePicker
              id="WasteDataRemovalExit"
              label={fields.data.removalExitDate.label}
              name={fields.data.removalExitDate.path}
              imperialName={fields.data.removalExitDateUS.path}
              isImperial={props.isImperial}
              form={form}
              validateDate={exitDateValidation}
              warningMessage={removalExitDateWarning}
              onFocus={() => {
                exitDatePopulation.onFocus()
                form.trigger(fields.data.removalExitDate.path)
              }}
            />

            <FormItem
              label={fields.data.consignmentId.label}
              name={fields.data.consignmentId.path}
              id="wasteDataConsignmentId"
              rules={Rules.MandatoryString}
              simpleDataValidationRules={CheckConsignmentOrDeliveryNumber}
            />

            <FormSpace />

            <AutoCompleteFormItem
              label={fields.data.subcontractorName.label}
              name={fields.data.subcontractorName.path}
              id="wasteDataSubContractorName"
              simpleDataValidationRules={v =>
                CheckSubContractor(v, props.projectName)
              }
            />

            <FormItem
              label={fields.data.originSiteAddress.label}
              name={fields.data.originSiteAddress.path}
              id="wasteDataOriginSiteAddress"
              simpleDataValidationRules={v => CheckAddress(v, null)}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>

            {journeyDistanceMiles > 1000 && (
              <p className="validation-warning">
                Calculated journey distance exceeds recommended limit. Please
                confirm addresses are correct.
              </p>
            )}

            <KnownAddressInput
              form={form}
              addressName="Origin Site"
              knownlabel={fields.data.originSiteAddressKnown.label}
              knownPropertyPath={fields.data.originSiteAddressKnown.path}
              givenPropertyPath={fields.data.originSiteAddress.path}
              enteredPropertyPath={fields.data.originSiteAddressEntered.path}
              coordinatesPropertyPath={
                fields.data.originSiteAddressCoordinates.path
              }
              projectCountry={props.projectCountry}
            />

            <AutoCompleteFormItem
              label={fields.data.classificationCode.label}
              name={fields.data.classificationCode.path}
              id="wasteDataSICCode"
              simpleDataValidationRules={CheckWasteSICCode}
            />

            <FormSpace />

            <AutoCompleteFormItem
              label={fields.data.vehicleRegistration.label}
              name={fields.data.vehicleRegistration.path}
              id="wasteDataVehicleReg"
              simpleDataValidationRules={CheckVehicleReg}
              uppercaseInput={true}
            />
          </>
        )}
      </Section>

      <Section {...props}>
        {({ firstInputRef }) => (
          <>
            {removalDate && (
              <CarrierPresetSelect onChange={handleCarrierSelectChange} />
            )}
            {!removalDate && (
              <p className="warning">
                Please enter a Collection Arrival Date before selecting a
                Carrier
              </p>
            )}

            <FormSpace />

            <AutoCompleteFormItem
              label={fields.data.carrierName.label}
              name={fields.data.carrierName.path}
              id="wasteDataCarrierName"
              inputRef={firstInputRef}
              simpleDataValidationRules={CheckCarrierName}
            />

            <AutoCompleteFormItem
              label={fields.data.carrierLicenceId.label}
              name={fields.data.carrierLicenceId.path}
              id="wasteDataCarrierLicence"
              simpleDataValidationRules={CheckCarrierLicence}
            />
          </>
        )}
      </Section>

      <Section {...props}>
        {({ firstInputRef }) => (
          <>
            {removalDate && (
              <FacilityPresetSelect
                setPermitPresentedTrue={setPermitPresentedTrue}
                onChange={handleFacilitySelectChange}
              />
            )}
            {!removalDate && (
              <p className="warning">
                Please enter a Collection Arrival Date before selecting a
                Facility
              </p>
            )}

            <FormSpace />

            <AutoCompleteFormItem
              label={fields.data.facilityOperatorName.label}
              name={fields.data.facilityOperatorName.path}
              id="wasteDataFacilityOperatorName"
              inputRef={firstInputRef}
            />

            <FormItem
              label={fields.data.facilitySiteAddress.label}
              name={fields.data.facilitySiteAddress.path}
              id="wasteDataFacilitySiteAddress"
              simpleDataValidationRules={v =>
                CheckAddress(v, knownFacilitySiteAddress)
              }
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>
            <KnownAddressInput
              form={form}
              addressName="Facility Site"
              knownlabel={fields.data.facilitySiteAddressKnown.label}
              knownPropertyPath={fields.data.facilitySiteAddressKnown.path}
              givenPropertyPath={fields.data.facilitySiteAddress.path}
              enteredPropertyPath={fields.data.facilitySiteAddressEntered.path}
              coordinatesPropertyPath={
                fields.data.facilitySiteAddressCoordinates.path
              }
              projectCountry={props.projectCountry}
            />

            <NarrowFormRowWithWarnings>
              <div className="reduced-field">
                <AutoCompleteFormItem
                  label={fields.data.facilityPermitId.label}
                  name={fields.data.facilityPermitId.path}
                  id="wasteDataFacilityPermit"
                  simpleDataValidationRules={CheckWastePermit}
                />
              </div>
              <div>
                <FormItem
                  label="&nbsp;"
                  name={fields.data.facilityPermitPresented.path}
                  style={{ maxWidth: 0 }}
                >
                  <input type="hidden" />
                </FormItem>
                <Checkbox
                  checked={permitPresentedChecked}
                  onChange={handlePermitPresentedChange}
                  style={{ paddingBottom: '6px' }}
                >
                  {fields.data.facilityPermitPresented.label}
                </Checkbox>
              </div>
            </NarrowFormRowWithWarnings>
          </>
        )}
      </Section>

      <Section {...props}>
        {({ firstInputRef }) => (
          <>
            <AutoCompleteFormItem
              label={fields.data.receiverReferenceNumber.label}
              name={fields.data.receiverReferenceNumber.path}
              inputRef={firstInputRef}
              id="wasteDataReceiverReference"
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
            />

            <AutoCompleteFormItem
              label={fields.data.supplierReferenceNumber.label}
              name={fields.data.supplierReferenceNumber.path}
              id="wasteDataSupplierReference"
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
            />

            <FormItem
              label={fields.data.notes.label}
              name={fields.data.notes.path}
              id="wasteDataNotes"
              simpleDataValidationRules={CheckNotes}
            >
              <Input.TextArea autoSize rows={1} />
            </FormItem>
          </>
        )}
      </Section>
    </>
  )
}

export const NarrowFormRowWithWarnings = styled.div`
  display: flex;
  justify-content: space-between;

  div.reduced-field {
    overflow-x: visible;
    max-width: 55.5%;
    > div {
      overflow: visible;
    }
  }

  p.validation-warning {
    width: 180%;
  }
`
